import { FC, ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../../store';
import { selectIdentity } from '../../store/entities/identity';
import { hasOtherRoleThan } from '../../utils/roles';
import { SectionHeader } from '../common/SectionHeader';
import { ContentWrapperColumn } from '../layout/commonLayout';
import { CompSettTabNotification } from './CompSettTabNotification';
import { CompSettTabPages } from './CompSettTabPages';
import { Tab } from '@mui/material';
import { QcsTabs } from '../common/basic/QcsTabs';
import { useTranslation } from 'react-i18next';
import { CompSettTabHandover } from './CompSettTabHandover';
import { useBusinessSettings } from '../../hooks/useBusinessSettings';
import { CompSettTabGeneral } from './CompSettTabGeneral';
import { CompSettTabAppearance } from './CompSettTabAppearance';
import { CompSettTabLanguages } from './CompSettTabLanguages';
import { CompSettTabBusiness } from './CompSettTabBusiness';
import { UserRole } from '@api/models/enums/UserRole';

//The id is also the URL segment, so every tab can be linked to directly as /settings/<id>.
type SettingsTabId = 'general' | 'pages' | 'notification' | 'appearance' | 'languages' | 'business' | 'handover';

interface SettingsTab {
  id: SettingsTabId;
  label: string;
  content: ReactNode;
}

export const Settings: FC = () => {
  const businessSettings = useBusinessSettings();
  const identity = useAppSelector(selectIdentity);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tabId } = useParams<{ tabId: SettingsTabId }>();

  const tabs: SettingsTab[] = [
    { id: 'general', label: t('settings.companyPages.general'), content: <CompSettTabGeneral /> },
    { id: 'pages', label: t('settings.companyPages.title'), content: <CompSettTabPages /> },
    ...(hasOtherRoleThan(identity.roles, [UserRole.ManagerOsah])
      ? [{ id: 'notification' as const, label: t('qcsCompanySettings.notification'), content: <CompSettTabNotification /> }]
      : []),
    { id: 'appearance', label: t('settings.appearance'), content: <CompSettTabAppearance /> },
    { id: 'languages', label: t('settings.languages'), content: <CompSettTabLanguages /> },
    { id: 'business', label: t('settings.business'), content: <CompSettTabBusiness /> },
    ...(businessSettings?.workplaceHandoverEnabled
      ? [{ id: 'handover' as const, label: t('handoverSettings.title'), content: <CompSettTabHandover /> }]
      : []),
  ];

  const activeTab = tabs.find((tab) => tab.id === tabId) ?? tabs[0];

  return (
    <ContentWrapperColumn>
      <SectionHeader title="settings.title" />
      <QcsTabs value={activeTab.id} onChange={(_event, id: SettingsTabId) => navigate(`/settings/${id}`)}>
        {tabs.map((tab) => (
          <Tab key={tab.id} value={tab.id} label={tab.label} />
        ))}
      </QcsTabs>
      {activeTab.content}
    </ContentWrapperColumn>
  );
};
